﻿import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import * as Sentry from "@sentry/react";
import { AuthProvider } from "./contexts/AuthContext";
import { msalConfig } from "./config/authConfig";
import App from "./App.tsx";
import "@fontsource-variable/roboto";
import "./index.css";

// Initialize Sentry for error tracking and performance monitoring
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE || "1.0.0",
  
  integrations: [
    // Browser tracing for performance monitoring
    Sentry.browserTracingIntegration(),
    // Capture React component errors with session replay
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  
  // Performance monitoring sample rate (adjust for production)
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  
  // Session replay sample rate
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  
  // Enable stack traces for errors
  attachStacktrace: true,
  
  // Capture unhandled promise rejections
  beforeSend(event, hint) {
    // You can modify or filter events here
    const error = hint.originalException;
    if (error && typeof error === 'object' && 'message' in error) {
      // Add custom context for network errors
      if ((error as Error).message?.includes('Network Error') || 
          (error as Error).message?.includes('ERR_CONNECTION')) {
        event.tags = {
          ...event.tags,
          error_type: 'network_error',
        };
      }
    }
    return event;
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const msalInstance = new PublicClientApplication(msalConfig);
await msalInstance.initialize();

// Handle redirect promise before rendering
// This ensures MSAL processes the auth callback before React Router
await msalInstance.handleRedirectPromise().catch((error) => {
  console.error('MSAL redirect error:', error);
  Sentry.captureException(error, {
    tags: { component: 'msal', action: 'redirect' },
  });
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <div className="flex min-h-screen items-center justify-center bg-background px-4">
          <div className="max-w-md rounded-lg border bg-card p-8 text-center">
            <img src="/deheus-primary-logo.svg" alt="De Heus" className="mx-auto h-12 w-12" />
            <h1 className="mt-4 text-xl font-semibold">Something went wrong</h1>
            <p className="mt-1 text-sm text-muted-foreground">An unexpected error occurred. Reloading usually fixes it.</p>
            <p className="mt-3 break-words text-xs text-muted-foreground">{error?.toString()}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-6 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Reload page
            </button>
          </div>
        </div>
      )}
      onError={(error) => {
        Sentry.captureException(error);
      }}
    >
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <App />
          </AuthProvider>
        </QueryClientProvider>
      </MsalProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);
